/**
 * fescue-globals.js
 * =================
 * Central JS for Fescue & Dunes logo overlay system.
 *
 * Load ONCE in theme.liquid <head> with defer:
 *   <script src="{{ 'fescue-globals.js' | asset_url }}" defer></script>
 *
 * ── PRIORITY CHAIN ───────────────────────────────────────────
 *
 *  P1  Club/metaobject page visited
 *      - Logos sourced from metaobject (3 logos per course)
 *      - Written to customLogoSources cookie
 *      - Persists across full user journey until another club page visited
 *
 *  P2  customLogoSources cookie already set (journey persistence)
 *      - User visited a club page previously in this session
 *      - Keep showing those logos on all non-club pages
 *
 *  P3  favorite_clubs cookie set (no club page visited)
 *      - User has favorited clubs via heart icon
 *      - Show logos from all favorited courses
 *
 *  P4  Theme defaults (nothing else applies)
 *      - window.fcDefaultLogos set by Liquid in theme.liquid
 *      - Static fallback defined in theme customizer
 *
 * ── DATA BRIDGE FROM LIQUID ──────────────────────────────────
 *
 *  Set these globals in theme.liquid BEFORE this file runs:
 *
 *  window.fcDefaultLogos   {string[]}  — P4 fallback logo URLs (Liquid rendered)
 *  window.fcPageLogos      {string[]}  — P1 logos from current page metaobject
 *                                        (3 logos, set only on club/metaobject pages)
 *  window.coursesData      {object[]}  — all courses with handle + logo
 *                                        (populated by featured-clubs.liquid or
 *                                         header-courses.liquid section blocks)
 *  window.fcAnimationMs    {number}    — ms between logo rotations (default 4500)
 *
 * ── PUBLIC API ───────────────────────────────────────────────
 *
 *  window.CookieManager                — cookie read/write for favorites + logos
 *  window.LogoEngine.resolve()         — run priority chain, return logo array
 *  window.LogoEngine.init()            — resolve + start animation
 *  window.LogoEngine.restart()         — stop existing interval, re-resolve, restart
 *  window.getLogoByHandle(handle)      — lookup logo URL from window.coursesData
 */

(function (w) {
  'use strict';


  // ============================================================
  // SECTION 1 — COOKIE MANAGER
  // ============================================================
  // Handles all cookie read/write for:
  //   - favorite_clubs      : JSON array of course handles
  //   - customLogoSources   : JSON array of logo URLs (P1/P2 persistence)

  w.CookieManager = w.CookieManager || {

    // ── Core cookie helpers ───────────────────────────────────

    set: function (name, value, days) {
      var date = new Date();
      date.setTime(date.getTime() + (days * 24 * 60 * 60 * 1000));
      document.cookie = name + '=' + value + ';expires=' + date.toUTCString() + ';path=/';
    },

    get: function (name) {
      var nameEQ = name + '=';
      var ca = document.cookie.split(';');
      for (var i = 0; i < ca.length; i++) {
        var c = ca[i];
        while (c.charAt(0) === ' ') c = c.substring(1);
        if (c.indexOf(nameEQ) === 0) return c.substring(nameEQ.length);
      }
      return null;
    },

    remove: function (name) {
      document.cookie = name + '=;expires=Thu, 01 Jan 1970 00:00:00 UTC;path=/;';
    },

    // ── Favorite clubs ────────────────────────────────────────
    // Stored as: favorite_clubs = encodeURIComponent(JSON.stringify([handle, ...]))

    getFavoriteClubs: function () {
      var raw = this.get('favorite_clubs');
      if (!raw) return [];
      try { return JSON.parse(decodeURIComponent(raw)); }
      catch (e) { console.warn('[CookieManager] Could not parse favorite_clubs:', e); return []; }
    },

    setFavoriteClubs: function (clubs) {
      this.set('favorite_clubs', encodeURIComponent(JSON.stringify(clubs)), 365);
    },

    addFavoriteClub: function (handle) {
      var clubs = this.getFavoriteClubs();
      if (clubs.indexOf(handle) === -1) {
        clubs.push(handle);
        this.setFavoriteClubs(clubs);
        return true;
      }
      return false;
    },

    removeFavoriteClub: function (handle) {
      var clubs = this.getFavoriteClubs();
      var next = clubs.filter(function (h) { return h !== handle; });
      if (next.length !== clubs.length) {
        this.setFavoriteClubs(next);
        return true;
      }
      return false;
    },

    isFavoriteClub: function (handle) {
      return this.getFavoriteClubs().indexOf(handle) !== -1;
    },

    clearFavoriteClubs: function () {
      this.remove('favorite_clubs');
    },

    // ── Club logo persistence (P1 → P2 journey) ──────────────
    // Stored as: customLogoSources = JSON.stringify([url, url, url])
    // Written when user visits a club/metaobject page (P1)
    // Read back on all subsequent pages (P2)

    getPersistedLogos: function () {
      var raw = this.get('customLogoSources');
      if (!raw || raw === '') return null;
      try {
        var parsed = JSON.parse(raw);
        return (Array.isArray(parsed) && parsed.length > 0) ? parsed : null;
      }
      catch (e) { console.warn('[CookieManager] Could not parse customLogoSources:', e); return null; }
    },

    setPersistedLogos: function (logoArray) {
      // Store for 1 day — refreshed on every club page visit
      this.set('customLogoSources', JSON.stringify(logoArray), 1);
    },

    clearPersistedLogos: function () {
      this.remove('customLogoSources');
    }
  };

  // ── Legacy aliases ────────────────────────────────────────
  // Keep these so any existing theme.liquid inline scripts don't break

  w.setCookie        = function (n, v, d) { w.CookieManager.set(n, v, d); };
  w.getCookie        = function (n)       { return w.CookieManager.get(n); };
  w.setCustomLogos   = function (a, d)    { w.CookieManager.setPersistedLogos(a); };
  w.clearCachedLogos = function ()        { w.CookieManager.clearPersistedLogos(); };


  // ============================================================
  // SECTION 2 — COURSE LOGO LOOKUP
  // ============================================================
  // Looks up a logo URL by course handle from window.coursesData.
  // window.coursesData is populated by:
  //   - featured-clubs.liquid  (block-based section)
  //   - header-courses.liquid  (block-based section)
  // Both sections push entries into the same global array.

  w.getLogoByHandle = function (handle) {
    var data = w.coursesData || [];
    for (var i = 0; i < data.length; i++) {
      if (data[i].handle === handle && data[i].logo) {
        var logo = data[i].logo;
        // Normalise protocol-relative URLs (//cdn.shopify.com/...)
        return logo.indexOf('//') === 0 ? 'https:' + logo : logo;
      }
    }
    return null;
  };


  // ============================================================
  // SECTION 3 — LOGO ENGINE (priority resolver + animation)
  // ============================================================

  w.LogoEngine = {

    // ── resolve() ────────────────────────────────────────────
    // Runs the full priority chain.
    // Returns the resolved logo URL array and writes it to window.logoSources.

    resolve: function () {
      var logos = null;

      // ── P0: Facet filter override ───────────────────────────
      // Set by facets.liquid when user selects a club filter.
      // Highest priority — overrides everything including club page visit.
      // Cleared when filter is removed, restoring normal priority chain.
      if (w.fcFacetLogos && w.fcFacetLogos.length > 0) {
        console.log('[LogoEngine] P0 — facet filter logos:', w.fcFacetLogos);
        w.logoSources = w.fcFacetLogos;
        return w.fcFacetLogos;
      }

      // ── P1: Club/metaobject page ────────────────────────────
      // window.fcPageLogos is set inline by theme.liquid ONLY on
      // club pages / metaobject template pages (3 logos per course).
      // If present, write to cookie so it persists (becomes P2).
      if (w.fcPageLogos && w.fcPageLogos.length > 0) {
        console.log('[LogoEngine] P1 — club page logos:', w.fcPageLogos);
        w.CookieManager.setPersistedLogos(w.fcPageLogos);
        logos = w.fcPageLogos;
      }

      // ── P2: Persisted logos from prior club page visit ──────
      // customLogoSources cookie was written by a previous P1 visit.
      // User is now on a non-club page but the logo should follow them.
      if (!logos) {
        var persisted = w.CookieManager.getPersistedLogos();
        if (persisted) {
          console.log('[LogoEngine] P2 — persisted club logos from cookie:', persisted);
          logos = persisted;
        }
      }

      // ── P3: Favorite clubs ──────────────────────────────────
      // No club page visited — use logos from the user's favorited courses.
      // Requires window.coursesData to be populated by a section.
      if (!logos) {
        var handles = w.CookieManager.getFavoriteClubs();
        if (handles.length > 0) {
          var favLogos = handles
            .map(function (h) { return w.getLogoByHandle(h); })
            .filter(Boolean);
          if (favLogos.length > 0) {
            console.log('[LogoEngine] P3 — favorite club logos:', favLogos);
            logos = favLogos;
          }
        }
      }

      // ── P4: Theme defaults ──────────────────────────────────
      // Nothing else matched. Use the static logo set from theme customizer.
      // window.fcDefaultLogos is set by Liquid in theme.liquid.
      if (!logos) {
        var defaults = w.fcDefaultLogos || [];
        if (defaults.length > 0) {
          console.log('[LogoEngine] P4 — theme default logos:', defaults);
          logos = defaults;
        }
      }

      // Fallback to empty array so animation doesn't crash
      logos = logos || [];

      w.logoSources = logos;
      return logos;
    },

    // ── _animate() ───────────────────────────────────────────
    // Internal: display logos on all .logo-overlay elements with
    // staggered animation between each overlay instance.

    _currentIndex: 0,
    _isFirstRun: true,

    _updateOverlays: function () {
      var self = w.LogoEngine;
      var overlays = document.querySelectorAll('.logo-overlay');
      if (!overlays.length || !w.logoSources || !w.logoSources.length) return;

      var src = w.logoSources[self._currentIndex];

      overlays.forEach(function (overlay, i) {
        // First run: show immediately. Subsequent: stagger by 200ms per overlay
        var delay = self._isFirstRun ? 0 : i * 200;
        setTimeout(function () {
          overlay.innerHTML = '';
          var img = document.createElement('img');
          img.src = src;
          img.style.cssText = 'width:100%;height:100%;object-fit:contain;';
          overlay.appendChild(img);
        }, delay);
      });

      self._currentIndex = (self._currentIndex + 1) % w.logoSources.length;
      self._isFirstRun = false;
    },

    // ── init() ───────────────────────────────────────────────
    // Resolve priority chain then start animation.
    // Called once on DOMContentLoaded from theme.liquid.

    init: function () {
      var self = this;
      var logos = self.resolve();

      if (!logos.length) {
        console.warn('[LogoEngine] No logo sources found across all priorities.');
        return;
      }

      self._currentIndex = 0;
      self._isFirstRun = true;
      self._updateOverlays();

      var ms = w.fcAnimationMs || 4500;
      w.logoAnimationInterval = setInterval(function () {
        self._updateOverlays();
      }, ms);

      console.log('[LogoEngine] Animation started —', logos.length, 'logo(s),', ms, 'ms interval');
    },

    // ── restart() ────────────────────────────────────────────
    // Stop existing animation, re-resolve priority chain, restart.
    // Call this after any favorite add/remove or cookie change.

    restart: function () {
      var self = this;

      if (w.logoAnimationInterval) {
        clearInterval(w.logoAnimationInterval);
        w.logoAnimationInterval = null;
      }

      self._currentIndex = 0;
      self._isFirstRun = true;

      var logos = self.resolve();

      if (!logos.length) {
        console.warn('[LogoEngine] No logo sources found after restart.');
        return;
      }

      var ms = w.fcAnimationMs || 4500;
      self._updateOverlays();
      w.logoAnimationInterval = setInterval(function () {
        self._updateOverlays();
      }, ms);

      console.log('[LogoEngine] Animation restarted —', logos.length, 'logo(s)');
    }
  };

  // Legacy alias — product-card.liquid calls reinitLogoAnimation()
  w.reinitLogoAnimation = function () { w.LogoEngine.restart(); };


  // ============================================================
  // SECTION 4 — FEATURED CLUBS SECTION UI
  // ============================================================
  // Handles card grid hearts, favorites strip, and mobile carousel.
  // Called per-section via: FeaturedClubs.init(sectionId)
  // Data passed via:        window['fcCourses_' + sectionId]

  w.FeaturedClubs = w.FeaturedClubs || {

    init: function (sectionId) {
      var courses   = w['fcCourses_' + sectionId] || [];
      var grid      = document.getElementById('fc-grid-'       + sectionId);
      var dotsEl    = document.getElementById('fc-dots-'       + sectionId);
      var emptyEl   = document.getElementById('fc-empty-'      + sectionId);
      var filledEl  = document.getElementById('fc-filled-'     + sectionId);
      var favScroll = document.getElementById('fc-fav-scroll-' + sectionId);

      if (!grid) return;

      // ── Helpers ──────────────────────────────────────────

      function isMobile() {
        return w.matchMedia('(max-width: 768px)').matches;
      }

      // After any favorite change: re-render strip + restart logo animation
      function onFavoritesChanged() {
        syncHearts();
        renderFavorites();
        w.LogoEngine.restart();
      }

      // ── Heart buttons on cards ────────────────────────────

      function syncHearts() {
        var favs = w.CookieManager.getFavoriteClubs();
        grid.querySelectorAll('.fc-card__heart').forEach(function (btn) {
          btn.classList.toggle('is-favorited', favs.indexOf(btn.dataset.handle) !== -1);
        });
      }

      function attachHeartListeners() {
        grid.querySelectorAll('.fc-card__heart').forEach(function (btn) {
          btn.addEventListener('click', function (e) {
            e.preventDefault();
            e.stopPropagation();
            var handle = btn.dataset.handle;
            if (w.CookieManager.isFavoriteClub(handle)) {
              w.CookieManager.removeFavoriteClub(handle);
            } else {
              w.CookieManager.addFavoriteClub(handle);
            }
            onFavoritesChanged();
          });
        });
      }

      // ── Favorites strip ───────────────────────────────────

      function renderFavorites() {
        var favs = w.CookieManager.getFavoriteClubs();
        var favCourses = courses.filter(function (c) {
          return favs.indexOf(c.handle) !== -1;
        });

        if (favCourses.length === 0) {
          emptyEl.style.display  = '';
          filledEl.style.display = 'none';
          return;
        }

        emptyEl.style.display  = 'none';
        filledEl.style.display = '';
        favScroll.innerHTML    = '';

        favCourses.forEach(function (course) {
          var item = document.createElement('div');
          item.className = 'fc-fav-item';
          item.innerHTML =
            '<div class="fc-fav-item__box">' +
              (course.logo
                ? '<img src="' + course.logo + '" alt="' + course.title + '" loading="lazy" width="180" height="180">'
                : '') +
              '<button class="fc-fav-item__heart" aria-label="Remove from favorites" type="button">' +
                '<svg viewBox="0 0 24 25" fill="#f1e2cb" xmlns="http://www.w3.org/2000/svg">' +
                  '<path fill-rule="evenodd" clip-rule="evenodd" d="M21.0002 8.97903C21.0002 15.2234 12.0006 20.479 12.0006 20.479C12.0006 20.479 3.00024 15.1457 3.00024 8.99169C3.00024 6.47903 5.00024 4.47903 7.50024 4.47903C10.0002 4.47903 12.0002 7.47903 12.0002 7.47903C12.0002 7.47903 14.0002 4.47903 16.5002 4.47903C19.0002 4.47903 21.0002 6.47903 21.0002 8.97903Z" stroke="#c58a30" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>' +
                '</svg>' +
              '</button>' +
            '</div>' +
            '<span class="fc-fav-item__name">' + course.title + '</span>';

          // Remove from favorites when heart in strip is clicked
          item.querySelector('.fc-fav-item__heart').addEventListener('click', function (e) {
            e.preventDefault();
            e.stopPropagation();
            w.CookieManager.removeFavoriteClub(course.handle);
            onFavoritesChanged();
          });

          favScroll.appendChild(item);
        });
      }

      // ── Mobile 2×2 carousel ───────────────────────────────

      var currentPage    = 0;
      var cards          = Array.from(grid.querySelectorAll('.fc-card'));
      var CARDS_PER_PAGE = 4;

      function totalPages() {
        return Math.ceil(cards.length / CARDS_PER_PAGE);
      }

      function buildDots() {
        dotsEl.innerHTML = '';
        if (!isMobile()) return;
        for (var i = 0; i < totalPages(); i++) {
          var dot = document.createElement('button');
          dot.className = 'fc-dot' + (i === currentPage ? ' active' : '');
          dot.setAttribute('aria-label', 'Page ' + (i + 1));
          dot.dataset.page = i;
          dot.addEventListener('click', function () {
            goToPage(parseInt(this.dataset.page));
          });
          dotsEl.appendChild(dot);
        }
      }

      function goToPage(page) {
        currentPage = page;
        cards.forEach(function (card, idx) {
          card.style.display = (Math.floor(idx / CARDS_PER_PAGE) === currentPage) ? '' : 'none';
        });
        dotsEl.querySelectorAll('.fc-dot').forEach(function (dot, i) {
          dot.classList.toggle('active', i === currentPage);
        });
      }

      function applyCarousel() {
        if (isMobile()) {
          buildDots();
          goToPage(currentPage);
        } else {
          cards.forEach(function (card) { card.style.display = ''; });
          dotsEl.innerHTML = '';
        }
      }

      // Touch swipe for carousel
      var touchStartX = 0;
      grid.addEventListener('touchstart', function (e) {
        touchStartX = e.changedTouches[0].clientX;
      }, { passive: true });

      grid.addEventListener('touchend', function (e) {
        if (!isMobile()) return;
        var dx = e.changedTouches[0].clientX - touchStartX;
        if (Math.abs(dx) < 40) return;
        if (dx < 0 && currentPage < totalPages() - 1) goToPage(currentPage + 1);
        if (dx > 0 && currentPage > 0) goToPage(currentPage - 1);
      }, { passive: true });

      // Reapply carousel on resize
      var resizeTimer;
      w.addEventListener('resize', function () {
        clearTimeout(resizeTimer);
        resizeTimer = setTimeout(function () {
          currentPage = 0;
          applyCarousel();
        }, 200);
      });

      // ── Init this section instance ────────────────────────
      attachHeartListeners();
      syncHearts();
      renderFavorites();
      applyCarousel();
    }
  };

}(window));
